import { InputWithLabel } from '@adotel/components'
import { useMutation } from '@apollo/client'
import { Button, CircularProgress } from '@material-ui/core'
import LockIcon from '@material-ui/icons/Lock'
import LockOpenIcon from '@material-ui/icons/LockOpen'
import { Form, Formik } from 'formik'
import { useMemo, useState } from 'react'
import React from 'react'
import { toast } from 'react-toastify'

import PageHeader from '../../components/PageHeader'
import { RESET_PASSWORD_MUTATION } from '../../graphql/resetPassword'
import { getTranslate } from '../../localization'
import { useStyles } from './styles/index.styles'
import validationSchema from './validationSchema'

type ResetPasswordForm = {
  currentPassword: string
  newPassword: string
  repeatPassword: string
}

const RESET_PASSWORD_FORM_INIT_VALUES = {
  currentPassword: '',
  newPassword: '',
  repeatPassword: ''
}

export default function ChangePassword() {
  const classes = useStyles()
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [changePassword] = useMutation(RESET_PASSWORD_MUTATION, {
    onCompleted: (data) => {
      toast.success(data.resetPassword.message)
    },
    onError: (error) => {
      toast.error(error.message)
    }
  })

  const handleSubmit = async (values: ResetPasswordForm) => {
    await changePassword({
      variables: {
        oldPassword: values.currentPassword,
        newPassword: values.repeatPassword
      }
    })
  }

  const [showPassword, setshowPassword] = useState<boolean>(true)
  const passwordInputIcon = useMemo(
    () => (
      <div
        onClick={() => setshowPassword(!showPassword)}
        role="presentation"
        className={classes.passwordIconContainer}>
        {showPassword ? <LockIcon /> : <LockOpenIcon />}
      </div>
    ),
    [showPassword]
  )

  return (
    <Formik
      initialValues={RESET_PASSWORD_FORM_INIT_VALUES}
      onSubmit={handleSubmit}
      preventDefault
      validationSchema={validationSchema}>
      {({
        values,
        errors,
        touched,
        handleBlur,
        isSubmitting,
        handleChange
      }) => {
        return (
          <>
            <PageHeader title={getTranslate('Change Password')} />
            <Form className={classes.form}>
              <InputWithLabel
                inputSize="small"
                placeholder={getTranslate('Current Password')}
                label={getTranslate('Current Password')}
                id="currentPassword"
                name="currentPassword"
                type={showPassword ? 'password' : 'string'}
                fullWidth
                color="primary"
                value={values.currentPassword}
                onChange={handleChange}
                error={
                  touched.currentPassword && Boolean(errors.currentPassword)
                }
                helpertext={
                  errors.currentPassword && touched.currentPassword
                    ? getTranslate(errors.currentPassword || '')
                    : ''
                }
                onBlur={handleBlur}
                endIcon={passwordInputIcon}
                disabled={isSubmitting}
              />
              <InputWithLabel
                inputSize="small"
                label={getTranslate('New Password')}
                placeholder={getTranslate('New Password')}
                id="newPassword"
                name="newPassword"
                type={showPassword ? 'password' : 'string'}
                fullWidth
                value={values.newPassword}
                onChange={handleChange}
                error={touched.newPassword && Boolean(errors.newPassword)}
                helpertext={
                  errors.newPassword && touched.newPassword
                    ? getTranslate(errors.newPassword || '')
                    : ''
                }
                onBlur={handleBlur}
                endIcon={passwordInputIcon}
                disabled={isSubmitting}
              />
              <InputWithLabel
                inputSize="small"
                label={getTranslate('Repeat new password')}
                placeholder={getTranslate('Repeat new password')}
                id="repeatPassword"
                name="repeatPassword"
                type={showPassword ? 'password' : 'string'}
                fullWidth
                value={values.repeatPassword}
                onChange={handleChange}
                error={touched.repeatPassword && Boolean(errors.repeatPassword)}
                helpertext={
                  errors.repeatPassword && touched.repeatPassword
                    ? getTranslate(errors.repeatPassword || '')
                    : ''
                }
                onBlur={handleBlur}
                endIcon={passwordInputIcon}
                disabled={isSubmitting}
              />
              <div className={classes.buttonContainer}>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                  className={classes.button}>
                  {getTranslate('Change Password')}
                  {isSubmitting && (
                    <CircularProgress
                      size={16}
                      className={classes.circularProgress}
                    />
                  )}
                </Button>
              </div>
            </Form>
          </>
        )
      }}
    </Formik>
  )
}
